export function MyDecksTab({
  signedIn,
  deckListFetched,
  deckSummaries,
  settingCurrentDeckId,
  deletingDeckId,
  startingNewDeck,
  importingDeck,
  exportingDeckId,
  ringsdbConnected,
  ringsdbExportEnabled,
  onStartNewDeck,
  onImportDeck,
  onActivateDeck,
  onDeleteDeck,
  onExportDeck,
  onConnectRingsdb,
}) {
  if (!signedIn) {
    return (
      <div className="mx-auto max-w-7xl px-4 py-8">
        <p className="text-center text-slate-400">Sign in to view your saved decks.</p>
      </div>
    );
  }

  const deckActionsBusy =
    startingNewDeck ||
    importingDeck ||
    settingCurrentDeckId != null ||
    deletingDeckId != null ||
    exportingDeckId != null;

  return (
    <div className="mx-auto max-w-7xl px-4 py-8">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-100">My decks</h2>
          <p className="text-sm text-slate-400">
            Saved copies of your lists. The highlighted row is your current working deck.
          </p>
          {ringsdbExportEnabled && !ringsdbConnected && (
            <p className="mt-2 text-xs text-slate-500">
              Export to RingsDB requires a one-time{" "}
              <button
                type="button"
                onClick={() => onConnectRingsdb()}
                className="text-amber-200/90 underline decoration-amber-600/50 underline-offset-2 hover:text-amber-100"
              >
                Connect RingsDB
              </button>{" "}
              link.
            </p>
          )}
        </div>
        <div className="flex shrink-0 flex-wrap gap-2">
          <button
            type="button"
            onClick={() => onImportDeck()}
            disabled={deckActionsBusy}
            className="rounded-md border border-slate-600 bg-slate-800 px-4 py-2 text-sm font-medium text-slate-100 hover:border-amber-500/50 hover:bg-slate-700/80 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {importingDeck ? "Importing…" : "Import deck"}
          </button>
          <button
            type="button"
            onClick={() => void onStartNewDeck()}
            disabled={deckActionsBusy}
            className="rounded-md border border-amber-600/70 bg-amber-950/90 px-4 py-2 text-sm font-medium text-amber-100 shadow hover:bg-amber-900 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {startingNewDeck ? "Starting…" : "Start new deck"}
          </button>
        </div>
      </div>
      {!deckListFetched ? (
        <p className="mt-8 text-center text-sm text-slate-500">Loading decks…</p>
      ) : deckSummaries.length === 0 ? (
        <p className="mt-8 text-center text-slate-500">
          No saved decks yet. Use <span className="text-slate-400">Start new deck</span> or{" "}
          <span className="text-slate-400">Import deck</span> to begin.
        </p>
      ) : (
        <ul className="divide-y divide-slate-800 rounded-lg border border-slate-800 bg-slate-900/40">
          {deckSummaries.map((d) => (
            <li
              key={d.id}
              className={[
                "flex flex-col gap-3 px-4 py-4 sm:flex-row sm:items-center sm:justify-between",
                d.is_current ? "bg-amber-950/20" : "",
              ].join(" ")}
            >
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium text-slate-100">{d.name}</span>
                  {d.is_current && (
                    <span className="rounded bg-amber-950/80 px-2 py-0.5 text-xs font-medium text-amber-200">
                      Current
                    </span>
                  )}
                </div>
                <p className="mt-1 text-xs text-slate-500">
                  {d.total_cards} card{d.total_cards === 1 ? "" : "s"} · updated{" "}
                  {d.updated_at ? new Date(d.updated_at).toLocaleString() : "—"}
                </p>
              </div>
              <div className="flex shrink-0 flex-wrap items-center gap-2">
                {ringsdbExportEnabled && (
                  <button
                    type="button"
                    onClick={() => void onExportDeck(d.id)}
                    disabled={deckActionsBusy}
                    className="rounded-md border border-slate-600 bg-slate-800 px-3 py-2 text-sm text-slate-100 hover:border-amber-500/50 hover:bg-slate-700/80 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {exportingDeckId === d.id
                      ? "Exporting…"
                      : ringsdbConnected
                        ? "Export deck"
                        : "Connect RingsDB"}
                  </button>
                )}
                {!d.is_current && (
                  <button
                    type="button"
                    onClick={() => void onActivateDeck(d.id)}
                    disabled={deckActionsBusy}
                    className="rounded-md border border-slate-600 bg-slate-800 px-3 py-2 text-sm text-slate-100 hover:border-amber-500/50 hover:bg-slate-700/80 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {settingCurrentDeckId === d.id ? "Loading…" : "Set to current deck"}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => void onDeleteDeck(d.id, d.name)}
                  disabled={deckActionsBusy}
                  className="rounded-md border border-red-900/80 bg-red-950/50 px-3 py-2 text-sm text-red-200 hover:border-red-500/60 hover:bg-red-950/80 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {deletingDeckId === d.id ? "Deleting…" : "Delete"}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
